import { useState } from "react";
import { supabase } from "@/lib/supabase";
import { t } from "@/lib/i18n";
import {
  Award, ShieldCheck, FileText, Copy, HandHeart,
  CircleDollarSign, Package, Mail, Send, CheckCircle2,
} from "lucide-react";

type Tab = "certificates" | "caravana" | "messaging";

export default function PublicSections({ tab }: { tab: Tab }) {
  if (tab === "certificates") return <Certificates />;
  if (tab === "caravana") return <Caravana />;
  return <Messaging />;
}

function Certificates() {
  const [certNumber, setCertNumber] = useState("");
  const [docType, setDocType] = useState("report");
  const [downloadFormat, setDownloadFormat] = useState("pdf");
  const [showResult, setShowResult] = useState(false);

  const checkCertificate = () => {
    if (!certNumber.trim()) { alert(t("cert_alert")); return; }
    setShowResult(true);
  };

  const downloadDocument = (type: string) => {
    const fmt = downloadFormat === "pdf" ? "Official PDF Document" : downloadFormat === "secure_copy" ? "Certified Secure Copy" : "Print-Ready Clean Copy";
    alert(`${type === "pdf" ? t("cert_download_pdf_alert") : t("cert_download_copy_alert")} (${fmt})...`);
  };

  return (
    <div className="animate-fadeIn">
      <div className="card">
        <div className="mb-5 pb-3 border-b-2 border-gray-200">
          <h2 className="text-[#1C3988] flex items-center gap-3 text-xl font-bold">
            <Award className="w-6 h-6" /> {t("cert_title")}
          </h2>
        </div>
        <p className="text-gray-500 text-sm mb-5">
          {t("cert_desc")}
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-5">
          <div className="form-group">
            <label className="form-label">{t("lbl_certno")}</label>
            <input className="form-input" type="text" value={certNumber} onChange={(e) => setCertNumber(e.target.value)} placeholder={t("ph_certno")} />
          </div>
          <div className="form-group">
            <label className="form-label">{t("lbl_doctype")}</label>
            <select className="form-input" value={docType} onChange={(e) => setDocType(e.target.value)}>
              <option value="report">{t("opt_report")}</option>
              <option value="cert">{t("opt_cert")}</option>
              <option value="conduct">{t("opt_conduct")}</option>
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">{t("lbl_dlformat")}</label>
            <select className="form-input" value={downloadFormat} onChange={(e) => setDownloadFormat(e.target.value)}>
              <option value="pdf">{t("opt_pdf")}</option>
              <option value="secure_copy">{t("opt_seccopy")}</option>
              <option value="print_copy">{t("opt_printcopy")}</option>
            </select>
          </div>
        </div>

        <button onClick={checkCertificate} className="btn-cyan">
          <ShieldCheck className="w-5 h-5" /> {t("btn_checkdoc")}
        </button>

        {showResult && (
          <div className="mt-5 p-5 rounded-lg bg-[#F8FAFC] border-l-5 border-l-[#38B6FF] border border-[#E2E8F0]">
            <h3 className="text-[#7A2222] flex items-center gap-2 font-bold">
              <CheckCircle2 className="w-5 h-5" /> {t("doc_verified")}
            </h3>
            <p className="mt-2"><strong>{t("doc_title")}</strong> {t("doc_title_val")}</p>
            <p><strong>{t("doc_issued")}</strong> {t("doc_issued_val")}</p>
            <p><strong>{t("doc_stamp")}</strong> {t("doc_stamp_val")}</p>
            <div className="mt-4 flex gap-2.5 flex-wrap">
              <button className="btn-primary" onClick={() => downloadDocument("pdf")}>
                <FileText className="w-4 h-4" /> {t("btn_download_pdf")}
              </button>
              <button className="btn-cyan" onClick={() => downloadDocument("copy")}>
                <Copy className="w-4 h-4" /> {t("btn_download_copy")}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Caravana() {
  const openDonateModal = (type: string) => {
    if (type === "Monetary") {
      alert(t("caravana_donate_monetary_alert"));
    } else if (type === "Material") {
      alert(t("caravana_donate_material_alert"));
    } else {
      alert(t("caravana_donate_voluntary_alert"));
    }
  };

  return (
    <div className="animate-fadeIn">
      <div
        className="rounded-xl text-white p-12 text-center mb-6"
        style={{
          background: "linear-gradient(rgba(28,57,136,0.88), rgba(122,34,34,0.88)), url('https://images.unsplash.com/photo-1509062522246-3755977927d7?auto=format&fit=crop&w=1200&q=80')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <h2 className="text-3xl font-bold mb-2">{t("caravana_banner_title")}</h2>
        <p>{t("caravana_banner_sub")}</p>
      </div>

      <div className="card">
        <div className="mb-5 pb-3 border-b-2 border-gray-200">
          <h2 className="text-[#1C3988] flex items-center gap-3 text-xl font-bold">
            <HandHeart className="w-6 h-6" /> {t("caravana_what_title")}
          </h2>
        </div>

        <p className="leading-relaxed mb-4">
          {t("caravana_intro")}
        </p>
        <ul className="leading-relaxed mb-5 ml-5 list-disc space-y-1">
          <li>{t("caravana_list1")}</li>
          <li>{t("caravana_list2")}</li>
          <li>{t("caravana_list3")}</li>
          <li>{t("caravana_list4")}</li>
        </ul>

        <h3 className="text-[#1C3988] mb-3 text-lg font-bold">{t("caravana_ubuntu_title")}</h3>
        <p className="leading-relaxed mb-5">
          {t("caravana_ubuntu_desc")}
        </p>

        <h3 className="text-[#1C3988] mb-3 text-lg font-bold">{t("caravana_pillars_title")}</h3>
        <ul className="leading-relaxed mb-5 ml-5 list-disc space-y-1">
          <li>{t("caravana_pillar1")}</li>
          <li>{t("caravana_pillar2")}</li>
          <li>{t("caravana_pillar3")}</li>
          <li>{t("caravana_pillar4")}</li>
        </ul>

        <h3 className="text-[#1C3988] mb-3 text-lg font-bold">{t("caravana_impact_title")}</h3>
        <p className="leading-relaxed mb-5">
          {t("caravana_impact_desc")}
        </p>

        <h3 className="text-[#1C3988] mb-3 text-lg font-bold">{t("caravana_conclusion_title")}</h3>
        <p className="leading-relaxed mb-6">
          {t("caravana_conclusion_desc")}
        </p>

        <h3 className="text-[#1C3988] mb-4 text-lg font-bold">{t("caravana_support_title")}</h3>
        <div className="flex gap-3.5 flex-wrap">
          <button className="btn-primary" onClick={() => openDonateModal("Monetary")}>
            <CircleDollarSign className="w-5 h-5" /> {t("caravana_donate_monetary")}
          </button>
          <button className="btn-cyan" onClick={() => openDonateModal("Material")}>
            <Package className="w-5 h-5" /> {t("caravana_donate_material")}
          </button>
          <button className="btn-success" onClick={() => openDonateModal("Voluntary")}>
            <HandHeart className="w-5 h-5" /> {t("caravana_voluntary")}
          </button>
        </div>
      </div>
    </div>
  );
}

function Messaging() {
  const [recipient, setRecipient] = useState("preschool");
  const [senderName, setSenderName] = useState("");
  const [contactInfo, setContactInfo] = useState("");
  const [body, setBody] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const sendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    const { error: insertError } = await supabase.from("messages").insert({
      recipient,
      sender_name: senderName,
      contact_info: contactInfo,
      body,
    });
    setLoading(false);
    if (insertError) {
      setError(t("msg_err_send"));
      return;
    }
    setSuccess(true);
    setSenderName(""); setContactInfo(""); setBody("");
    setTimeout(() => setSuccess(false), 5000);
  };

  const recipientLabels: Record<string, string> = {
    preschool: t("opt_preschool"),
    primary: t("opt_primary"),
    secondary: t("opt_secondary"),
    safety: t("opt_safety"),
  };

  return (
    <div className="animate-fadeIn">
      <div className="card">
        <div className="mb-5 pb-3 border-b-2 border-gray-200">
          <h2 className="text-[#1C3988] flex items-center gap-3 text-xl font-bold">
            <Mail className="w-6 h-6" /> {t("msg_title")}
          </h2>
        </div>

        <form onSubmit={sendMessage}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-5">
            <div className="form-group">
              <label className="form-label">{t("lbl_recipient")}</label>
              <select className="form-input" value={recipient} onChange={(e) => setRecipient(e.target.value)} required>
                <option value="preschool">{t("opt_preschool")}</option>
                <option value="primary">{t("opt_primary")}</option>
                <option value="secondary">{t("opt_secondary")}</option>
                <option value="safety">{t("opt_safety")}</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">{t("lbl_sendername")}</label>
              <input className="form-input" type="text" value={senderName} onChange={(e) => setSenderName(e.target.value)} required placeholder={t("ph_sendername")} />
            </div>
            <div className="form-group">
              <label className="form-label">{t("lbl_contactinfo")}</label>
              <input className="form-input" type="text" value={contactInfo} onChange={(e) => setContactInfo(e.target.value)} required placeholder={t("ph_contactinfo")} />
            </div>
          </div>

          <div className="form-group mb-5">
            <label className="form-label">{t("lbl_msgbody")}</label>
            <textarea
              className="form-input"
              rows={5}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              required
              placeholder={t("ph_msgbody")}
            />
          </div>

          {error && <div className="text-[#D32F2F] bg-red-50 p-3 rounded-md text-sm mb-4">{error}</div>}
          {success && (
            <div className="flex items-center gap-2 text-[#2E7D32] bg-green-50 p-3 rounded-md text-sm mb-4">
              <CheckCircle2 className="w-4 h-4" /> {t("msg_success_prefix")} {recipientLabels[recipient]}. {t("msg_success_suffix")}
            </div>
          )}

          <button type="submit" disabled={loading} className="btn-primary">
            <Send className="w-5 h-5" /> {loading ? t("msg_btn_sending") : t("btn_sendmsg")}
          </button>
        </form>
      </div>
    </div>
  );
}
